/**
 * Visualización del progreso de loyalty del cliente.
 * count = tickets actuales, max = tickets necesarios para reward.
 *
 * Ej. matcha bar: 5 tickets = 1 matcha gratis.
 *
 * Usa CSS var --brand-primary para color del progreso (rebrandeable por tenant).
 */
export function TicketDisplay({
  count,
  max,
  rewardLabel = "Tu reward"
}: {
  count: number;
  max: number;
  rewardLabel?: string;
}) {
  const filled = Math.min(count, max);
  const remaining = Math.max(0, max - count);
  const isComplete = count >= max;
  const progress = Math.min(100, (count / max) * 100);

  return (
    <div className="rounded-3xl bg-white/85 p-5 ring-1 ring-leaf-100 backdrop-blur-sm">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider text-leaf-700">Tus puntos</p>
        <p className="font-display text-lg font-semibold text-leaf-950">
          {count} <span className="text-leaf-400">/ {max}</span>
        </p>
      </div>

      <div className="mb-3 flex items-center gap-1.5">
        {Array.from({ length: max }).map((_, i) => (
          <StarCircle key={i} filled={i < filled} />
        ))}
        <svg className="mx-1 h-4 w-4 text-leaf-400" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M10.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L13.586 11H4a1 1 0 110-2h9.586l-3.293-3.293a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
        <GiftCircle active={isComplete} />
      </div>

      <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-leaf-100">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${progress}%`,
            backgroundColor: "var(--brand-primary, #4a6921)"
          }}
        />
      </div>

      <p className="text-center text-sm text-leaf-700">
        {isComplete
          ? `🎉 ¡${rewardLabel} desbloqueado!`
          : `${remaining} ${remaining === 1 ? "compra más" : "compras más"} para tu reward`}
      </p>
    </div>
  );
}

function StarCircle({ filled }: { filled: boolean }) {
  return (
    <div
      className={`grid h-8 w-8 place-items-center rounded-full border-2 transition ${
        filled ? "border-leaf-500 bg-leaf-50" : "border-leaf-200 bg-white"
      }`}
    >
      <svg
        className={`h-4 w-4 ${filled ? "text-leaf-600" : "text-leaf-300"}`}
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    </div>
  );
}

function GiftCircle({ active }: { active: boolean }) {
  return (
    <div
      className="grid h-10 w-10 place-items-center rounded-full text-white shadow-sm"
      style={{
        backgroundColor: active ? "var(--brand-primary, #4a6921)" : "#d1d5db"
      }}
    >
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 12v10H4V12" /><path d="M2 7h20v5H2z" /><path d="M12 22V7" />
        <path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z" />
      </svg>
    </div>
  );
}
